/* eslint-disable arrow-parens */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BsFillPauseFill, BsFillPlayFill, BsVolumeMuteFill, BsVolumeUpFill } from "react-icons/bs";
import landingvideo from "../../../assets/videos/landingvideo.mp4";

import "./landingPage.css";

/**
 * Hero video source — change this single line to swap the video.
 * A direct file URL (.mp4 / .webm) renders in a <video> player, a YouTube
 * link renders as an embed instead, so either kind of URL can be dropped in.
 *
 * Current source: https://www.pexels.com/download/video/6290537/
 * Other renditions of the same clip:
 *   4K   .../6290537-uhd_3840_2160_30fps.mp4  (12.6 MB)
 *   720p .../6290537-hd_1280_720_30fps.mp4    (1.1 MB)
 */
const HERO_VIDEO_URL = landingvideo;

const HERO_VIDEO_TITLE = "Nexcaliber overview";

const getYouTubeId = (url: string): string | null => {
	const match = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
	return match ? match[1] : null;
};

const LandingPage: React.FC = () => {
	const navigate = useNavigate();
	const videoRef = useRef<HTMLVideoElement>(null);
	// once the visitor uses the controls, stop trying to autoplay over them
	const userTookOverRef = useRef(false);

	const [isPlaying, setIsPlaying] = useState(true);
	const [isMuted, setIsMuted] = useState(true);

	const youTubeId = getYouTubeId(HERO_VIDEO_URL);

	const goToContact = useCallback(() => {
		navigate("/contact");
	}, [navigate]);

	// Browsers only autoplay muted video, and React applies `muted` as a property
	// after the element is created — too late for Chrome's autoplay check. So mute
	// imperatively and start playback ourselves, showing the play button if refused.
	const startPlayback = useCallback(() => {
		const video = videoRef.current;
		if (!video || userTookOverRef.current || !video.paused) return;

		video.muted = true;
		const attempt = video.play();

		if (attempt && typeof attempt.then === "function") {
			attempt.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
		}
	}, []);

	useEffect(() => {
		startPlayback();
	}, [startPlayback]);

	const togglePlay = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;

		userTookOverRef.current = true;

		if (video.paused) {
			void video.play().catch(() => setIsPlaying(false));
			setIsPlaying(true);
		} else {
			video.pause();
			setIsPlaying(false);
		}
	}, []);

	const toggleMute = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;

		userTookOverRef.current = true;
		video.muted = !video.muted;
		setIsMuted(video.muted);
	}, []);

	return (
		<main className="lp-root">
			<section className="lp-hero">
				<div className="lp-hero-glow lp-hero-glow-one" aria-hidden="true" />
				<div className="lp-hero-glow lp-hero-glow-two" aria-hidden="true" />

				<div className="lp-shell lp-hero-grid">
					<div className="lp-hero-copy">
						<h1 className="lp-hero-title">We save you money.</h1>

						<p className="lp-hero-subtitle">
							Grow your business&rsquo;s savings by lowering healthcare costs.
						</p>

						<blockquote className="lp-hero-quote">
							&ldquo;There are four ways in which you can spend money. You can spend your own money on
							yourself. When you do that, why then you really watch out what you&rsquo;re doing, and you
							try to get the most for your money.&rdquo; - Milton Friedman
						</blockquote>

						<button type="button" className="lp-btn lp-btn-primary" onClick={goToContact}>
							GET STARTED
						</button>
					</div>

					<div className="lp-hero-media">
						<div className="lp-video-frame">
							{youTubeId ? (
								<iframe
									className="lp-video"
									src={`https://www.youtube.com/embed/${youTubeId}?rel=0`}
									title={HERO_VIDEO_TITLE}
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
									allowFullScreen
								/>
							) : (
								<>
									<video
										ref={videoRef}
										className="lp-video"
										src={HERO_VIDEO_URL}
										title={HERO_VIDEO_TITLE}
										autoPlay
										muted
										loop
										playsInline
										preload="auto"
										onCanPlay={startPlayback}
										onPlay={() => setIsPlaying(true)}
										onPause={() => setIsPlaying(false)}
									/>

									{!isPlaying && (
										<button
											type="button"
											className="lp-video-play-overlay"
											onClick={togglePlay}
											aria-label="Play video"
										>
											<span className="lp-video-play-icon">
												<BsFillPlayFill />
											</span>
										</button>
									)}

									<div className="lp-video-controls">
										<button
											type="button"
											onClick={togglePlay}
											aria-label={isPlaying ? "Pause video" : "Play video"}
										>
											{isPlaying ? <BsFillPauseFill /> : <BsFillPlayFill />}
										</button>
										<button
											type="button"
											onClick={toggleMute}
											aria-label={isMuted ? "Unmute video" : "Mute video"}
										>
											{isMuted ? <BsVolumeMuteFill /> : <BsVolumeUpFill />}
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</section>
		</main>
	);
};

export default LandingPage;
